import { useEffect, useLayoutEffect, useRef } from 'react';

import { inputActions } from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import {
    DOUBLE_CLICK_MS,
    getChordCode,
    getEventModifiers
} from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import {
    isContextActive,
    resolveInputContext
} from 'app/modules/wallforming/components/input-binding/utils/inputContextUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import type { InputChord } from 'app/modules/wallforming/components/input-binding/types/inputChord';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

const lastPress = {
    event: null as MouseEvent | null,
    count: 1 as 1 | 2,
    button: -1,
    pointerId: -1,
    target: null as EventTarget | null,
    modifiers: [] as ReturnType<typeof getEventModifiers>,
    timeStamp: -Infinity
};

/** Click count of a press, memoized per event so every consumer of the same press agrees on it. */
export function getClickCount(event: MouseEvent): 1 | 2 {
    if (event !== lastPress.event) {
        const modifiers = getEventModifiers(event);
        const pointerId = event instanceof PointerEvent ? event.pointerId : -1;
        const elapsed = event.timeStamp - lastPress.timeStamp;
        const isDoubleClick =
            lastPress.count === 1 &&
            lastPress.button === event.button &&
            lastPress.pointerId === pointerId &&
            lastPress.target === event.target &&
            lastPress.modifiers.every((modifier, index) => modifier === modifiers[index]) &&
            lastPress.modifiers.length === modifiers.length &&
            elapsed >= 0 &&
            elapsed <= DOUBLE_CLICK_MS;
        Object.assign(lastPress, {
            event,
            count: isDoubleClick ? 2 : 1,
            button: event.button,
            pointerId,
            target: event.target,
            modifiers,
            timeStamp: event.timeStamp
        });
    }
    return lastPress.count;
}

export const isInputActionBound = (
    actionId: InputActionId,
    chord: InputChord,
    state: PlacementState = usePlacementStore.getState()
) => state.inputBindings.slots[actionId].includes(chord);

/** Bound to `chord`, valid in the current scene mode, and not suspended by key capture. */
export function matchesInputAction(
    actionId: InputActionId,
    chord: InputChord,
    state: PlacementState = usePlacementStore.getState()
): boolean {
    return (
        !state.inputCaptureTarget &&
        isContextActive(inputActions[actionId].contexts, resolveInputContext(state)) &&
        isInputActionBound(actionId, chord, state)
    );
}

/** Release ignores modifiers, which may already be up when the key itself is released. */
export const isInputActionReleased = (actionId: InputActionId, event: KeyboardEvent, state: PlacementState) =>
    state.inputBindings.slots[actionId].some((chord) => chord !== null && getChordCode(chord) === event.code);

type InputActionHandler = (event: Event) => void;
const actionHandlers = new Map<InputActionId, Set<InputActionHandler>>();

/** Event bus for commands whose behavior lives inside a component. */
export function emitInputAction(actionId: InputActionId, event: Event): void {
    actionHandlers.get(actionId)?.forEach((handler) => handler(event));
}

export function useInputActionHandler(actionId: InputActionId, handler: InputActionHandler): void {
    const handlerRef = useRef(handler);
    useLayoutEffect(() => {
        handlerRef.current = handler;
    });

    useEffect(() => {
        const listener: InputActionHandler = (event) => handlerRef.current(event);
        const handlers = actionHandlers.get(actionId) ?? new Set();
        actionHandlers.set(actionId, handlers.add(listener));
        return () => {
            handlers.delete(listener);
        };
    }, [actionId]);
}
